import { useQuery } from "@apollo/client";
import PageHeader from "theme/components/common/PageHeader";
import * as React from "react";
import Card from "react-bootstrap/Card";
import { toast } from "react-toastify";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import { GetSensors } from "../../utils/__generated__/GetSensors";
import apiClient from "../../api";
import { GET_SENSORS } from "../../utils/sensors";
import { getFormValueHandler } from "../../utils/forms";
import { gettext } from "../../i18n";
import FormControl from "react-bootstrap/FormControl";
import "./SensorEditPage.scss";

interface EditableSensor {
  id: number;
  name: string;
}
const SensorEditPage = () => {
  const [editedValues, setEditedValues] = React.useState<EditableSensor>();
  const [addingSensor, setAddingSensor] = React.useState<EditableSensor>();
  const { data: sensorQuery, refetch: refetchSensors } =
    useQuery<GetSensors>(GET_SENSORS);

  const setValueHandler = getFormValueHandler(
    editedValues as Record<string, any>,
    setEditedValues as (
      value: React.SetStateAction<Record<string, any>>,
    ) => void,
  );

  const onUpdateSensor = async (
    { id, name }: EditableSensor,
    action: string = "edit",
  ) => {
    setEditedValues(undefined);

    let requestPath = "";
    switch (action) {
      case "edit":
        requestPath = `/admin/edit-sensor`;
        break;
      case "delete":
        requestPath = `/admin/delete-sensor`;
        break;
      case "add":
        requestPath = `/admin/add-sensor`;
        break;
      default:
        throw new Error("Invalid action");
    }

    try {
      await apiClient.request(requestPath, {
        method: "POST",
        data: {
          id,
          name,
        },
      });
      toast.success("Success");
      refetchSensors();
      return true;
    } catch (e) {
      toast.error(gettext((e as any).message.toString()));
    }
    return false;
  };

  const onSaveEdited = () =>
    onUpdateSensor(editedValues as EditableSensor, "edit");

  const onSaveNewSensor = async () => {
    const result = await onUpdateSensor(addingSensor as EditableSensor, "add");
    if (result) {
      setAddingSensor(undefined);
    }
  };

  const onDeleteSensor = (id: number) => {
    if (
      !window.confirm(gettext("Are you sure you want to delete this sensor?"))
    ) {
      return;
    }
    onUpdateSensor({ id } as EditableSensor, "delete");
  };

  const sensors = sensorQuery?.sensor ? [...sensorQuery?.sensor] : [];
  sensors.sort((a, b) => (a.id > b.id ? 1 : -1));
  return (
    <div className="SensorEditPage">
      <PageHeader title="Edit Sensors" className="mb-3"></PageHeader>
      <Card className="mb-3">
        <Card.Body>
          <Table>
            <thead>
              <tr>
                <th>Sensor name</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sensors.map((sensor) => (
                <tr key={sensor.id}>
                  {editedValues?.id === sensor.id ? (
                    <>
                      <td data-label="Sensor name">
                        <FormControl
                          type="text"
                          placeholder="Enter Sensor name"
                          value={editedValues.name}
                          onChange={setValueHandler("name")}
                        />
                      </td>
                    </>
                  ) : (
                    <>
                      <td data-label="Sensor name">
                        {sensor.name || gettext("(no name)")}
                      </td>
                    </>
                  )}

                  <td className="actionButtons">
                    {editedValues?.id === sensor.id ? (
                      <>
                        <Button size="sm" onClick={onSaveEdited}>
                          Save
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setEditedValues(undefined);
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          onClick={() => {
                            setEditedValues({
                              ...sensor,
                            });
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => onDeleteSensor(sensor.id)}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              <tr>
                {addingSensor && (
                  <>
                    <td data-label="Sensor name">
                      <FormControl
                        type="text"
                        placeholder="Enter Sensor name"
                        value={addingSensor.name}
                        onChange={(e) => {
                          setAddingSensor({
                            ...addingSensor,
                            name: e.target.value,
                          });
                        }}
                      />
                    </td>
                    <td className="actionButtons">
                      <Button size="sm" onClick={() => onSaveNewSensor()}>
                        Save
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setAddingSensor(undefined)}
                      >
                        Cancel
                      </Button>
                    </td>
                  </>
                )}

                {!addingSensor && (
                  <td colSpan={3}>
                    <Button
                      size="sm"
                      onClick={() => {
                        setAddingSensor({
                          id: -1,
                          name: "",
                        });
                      }}
                    >
                      Add Sensor
                    </Button>
                  </td>
                )}
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SensorEditPage;
